/**
 * FU part D (D-F1): cached-first storage for insights surfaces.
 *
 * ONE localStorage entry per surface/project holds the LAST viewed payloads;
 * a repeat visit renders it with ZERO network and an honest banner. A fresh
 * fetch happens ONLY on explicit action (Refresh now / Apply / QA refresh /
 * overlay refresh) — successful responses overwrite the entry.
 *
 * Failure policy (FU-AC-005): corrupt JSON reads as a miss and removes the
 * entry; entries above the size ceiling are not written (and clear the stale
 * one); QuotaExceeded degrades to a silent skip. The cache NEVER throws.
 */

export interface InsightsCacheEntry<T> {
  savedAt: string;
  scopeKey: string;
  payloads: T;
}

const PREFIX = 'segmently:insights-cache:v1';
/** JSON above this is not worth localStorage churn (quota is ~5MB per origin). */
export const INSIGHTS_CACHE_MAX_BYTES = 1_500_000;
/** Entries older than this render the banner in warning tone (never expire reads). */
export const INSIGHTS_CACHE_STALE_MS = 24 * 60 * 60 * 1000;

export function reportsCacheKey(projectId: string): string {
  return `${PREFIX}:reports:${projectId}`;
}

export function overlayCacheKey(projectId: string, funnelId: string): string {
  return `${PREFIX}:overlay:${projectId}:${funnelId}`;
}

/**
 * Canonical scope identity: stable across key insertion order so the same
 * applied scope always produces the same string.
 */
export function canonicalScopeKey(scope: Record<string, unknown>): string {
  const sorted = Object.keys(scope)
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      const value = scope[key];
      if (value !== undefined && value !== null && value !== '') acc[key] = value;
      return acc;
    }, {});
  return JSON.stringify(sorted);
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function readInsightsCache<T>(key: string): InsightsCacheEntry<T> | null {
  const store = storage();
  if (!store) return null;
  let raw: string | null = null;
  try {
    raw = store.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as InsightsCacheEntry<T>;
    if (!parsed || typeof parsed !== 'object'
      || typeof parsed.savedAt !== 'string'
      || typeof parsed.scopeKey !== 'string'
      || parsed.payloads === undefined) {
      throw new Error('malformed cache entry');
    }
    return parsed;
  } catch {
    try {
      store.removeItem(key);
    } catch {
      // ignore — a broken entry we cannot remove still reads as a miss
    }
    return null;
  }
}

export function writeInsightsCache<T>(key: string, scopeKey: string, payloads: T, savedAt = new Date().toISOString()): boolean {
  const store = storage();
  if (!store) return false;
  const entry: InsightsCacheEntry<T> = { savedAt, scopeKey, payloads };
  let serialized: string;
  try {
    serialized = JSON.stringify(entry);
  } catch {
    return false;
  }
  if (serialized.length > INSIGHTS_CACHE_MAX_BYTES) {
    // Oversized payloads skip caching AND drop the stale entry — showing an
    // older snapshot as «the last report» would be dishonest.
    clearInsightsCache(key);
    return false;
  }
  try {
    store.setItem(key, serialized);
    return true;
  } catch {
    // QuotaExceededError et al — cached-first is an optimization, never a failure.
    return false;
  }
}

export function clearInsightsCache(key: string): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(key);
  } catch {
    // ignore
  }
}

export function isCacheStale(savedAt: string, nowMs: number = Date.now()): boolean {
  const savedMs = Date.parse(savedAt);
  if (!Number.isFinite(savedMs)) return true;
  return nowMs - savedMs > INSIGHTS_CACHE_STALE_MS;
}
